import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { customersAPI, usersAPI } from '@/utils/api';
import CustomerTransactions from '@/components/CustomerTransactions';
import ComprehensiveTransactions from '@/components/ComprehensiveTransactions';
import PaymentRelationships from '@/components/PaymentRelationships';
import CustomerAnamnese from '@/components/CustomerAnamnese';
import CustomerMergeDialog from '@/components/customers/CustomerMergeDialog';
import DuplicatesDialog from '@/components/customers/DuplicatesDialog';
import OnboardingInviteDialog from '@/components/customers/OnboardingInviteDialog';
import PhoneCleanupCard from '@/components/settings/PhoneCleanupCard';
import CardCampaignDialog from '@/components/customers/CardCampaignDialog';
import CustomerBodyTracking from '@/components/CustomerBodyTracking';
import CustomerNutrition from '@/components/CustomerNutrition';
import CustomerInBodyData from '@/components/customer/CustomerInBodyData';
import NutritionGoalsEditor from '@/components/nutrition/NutritionGoalsEditor';
import CustomerAfterburnSettings from '@/components/admin/CustomerAfterburnSettings';
// Refactored Customer Components
import { 
  CustomerListTable,
  QuickExportMenu,
  CreateCustomerDialog, 
  AddPointsDialog, 
  PointsHistoryDialog,
  InvoiceDialog,
  AddChildDialog,
  SendPaymentLinkDialog,
  IndividualPricingDialog,
  AddCardDialog,
  EditServicesDialog,
  SubscriptionHistoryDialog,
  RecurringSubscriptionDialog
} from '@/components/customers';
// Detail Tab Components
import { CustomerSettingsTab, CustomerBillingTab, CustomerPaymentsTab, CustomerOverviewTab, CustomerAccountTab, CustomerSeriesTab, CustomerCustomFieldsTab, CustomerNotesTab } from '@/components/customers/detail';
import { RepriceProgress } from '@/components/customers/RepriceProgress';
import CustomerTarif595Tab from '@/components/customers/detail/CustomerTarif595Tab';
import CustomerRecoveryTab from '@/components/customers/detail/CustomerRecoveryTab';
import { toast } from 'sonner';
import { Users, Search, Plus, Eye, CheckCircle, XCircle, FileText, Bell, Mail, MessageCircle, Trash2, History, Calendar, ChevronDown, ChevronUp, ExternalLink, X, Edit, Download, ClipboardList, FileDown, Key, Zap, Merge, User, Scale, HeartPulse, Apple, CreditCard, Wallet, Receipt, Repeat, Settings, Wrench, Smartphone, ListChecks, StickyNote, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('active'); // active, inactive, all
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  const [detailTabLoaded, setDetailTabLoaded] = useState({
    account: false,
    payments: false,
    billing: false,
    tracking: false,
    anamnese: false,
    nutrition: false,
    recovery: false
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [sendingAppLink, setSendingAppLink] = useState(false);
  const [customerPhotos, setCustomerPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(0); // 0, 1, 2, 3
  const [deactivateConfirmStep, setDeactivateConfirmStep] = useState(0); // NEW: 0, 1, 2, 3
  const [statusChangeLoading, setStatusChangeLoading] = useState(false); // Loading für Aktivieren/Deaktivieren
  const [addPointsDialogOpen, setAddPointsDialogOpen] = useState(false); // NEW
  // CHF-Gutschrift Dialog (Geschenkgutscheine / Kulanz / Rückerstattungen)
  const [addCreditDialogOpen, setAddCreditDialogOpen] = useState(false);
  const [addCreditFormData, setAddCreditFormData] = useState({
    amount: '',
    reason: '',
    send_notification: true,
  });
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [cardCampaignOpen, setCardCampaignOpen] = useState(false);
  const [invitingIncomplete, setInvitingIncomplete] = useState(false);
  const [repairingMerges, setRepairingMerges] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(null);

  const fetchDuplicateCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/customers/duplicates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDuplicateCount(res.data?.count ?? 0);
    } catch (_) { /* noop */ }
  };
  const [pointPackages, setPointPackages] = useState([]); // NEW
  const [subscriptionTemplates, setSubscriptionTemplates] = useState([]);
  const [pointsFormData, setPointsFormData] = useState({ // NEW
    package_id: '',
    custom_points: '',
    payment_method: 'cash',
    notes: ''
  });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false); // NEW
  const [pointTransactions, setPointTransactions] = useState([]); // NEW
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    studio_id: '',
  });
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '', // Geschlecht
    street: '',
    postal_code: '',
    city: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    height: '',
    suit_size: '', // EMS Anzugsgrösse
    payment_timing: 'sofort',
    auto_charge_card: true,
    individual_prices: {},
    individual_package_prices: {},
    individual_subscription_prices: {},
    discount_percent: 0,
    email_reminders_enabled: true,
    whatsapp_reminders_enabled: true,
    email_newsletters_enabled: true,
    whatsapp_newsletters_enabled: true,
    appointment_changes_notifications: true,
    appointment_confirmation_notifications: true,
    notification_channel: 'both',
    studio_id: '',
  });
  
  // NEW: Pending charges state
  const [pendingCharges, setPendingCharges] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [repriceJob, setRepriceJob] = useState(null); // {jobId, customerId, customerName}
  const [services, setServices] = useState([]);
  // Multi-Studio (P0): Studio-Liste für Customer-Form
  const [studios, setStudios] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/studios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudios(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, []);
  
  // NEW: Recurring subscription state (now supports multiple subscriptions)
  const [recurringSubscriptions, setRecurringSubscriptions] = useState([]); // Array
  const [activeSubscription, setActiveSubscription] = useState(null); // aktuell ausgewähltes Abo im Dialog
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    name: '',
    description: '',
    points_per_month: 100,
    price_per_month: 200,
    billing_cycle: 'monthly',
    payment_method: 'kreditkarte', // Immer Kreditkarte für Abos
    allowed_service_ids: [],
    // Flexible Initialzahlung (optional)
    initial_price: null,
    initial_points: null,
    // Auto-Reload Feature
    renewal_mode: 'monthly_25',
    low_balance_threshold: 2
  });
  
  // NEW: Payrexx payment tokens state
  const [paymentTokens, setPaymentTokens] = useState([]);
  const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);
  const [cardPaymentLink, setCardPaymentLink] = useState(null);
  const [pendingGatewayId, setPendingGatewayId] = useState(null);
  
  // NEW: Send link dialog
  const [sendLinkDialogOpen, setSendLinkDialogOpen] = useState(false);
  
  // NEW: Children management
  const [addChildDialogOpen, setAddChildDialogOpen] = useState(false);
  const [childFormData, setChildFormData] = useState({ name: '', date_of_birth: '' });
  
  // NEW: Customer appointments (Kontoübersicht)
  const [customerAppointments, setCustomerAppointments] = useState({ upcoming: [], past: [] });
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [showAllPastAppointments, setShowAllPastAppointments] = useState(false);
  const [showAllUpcomingAppointments, setShowAllUpcomingAppointments] = useState(false);
  
  // NEW: Detailed points info with package breakdown
  const [pointsDetails, setPointsDetails] = useState(null);
  const [loadingPointsDetails, setLoadingPointsDetails] = useState(false);
  
  // NEW: Latest body tracking data for profile display
  const [latestBodyTracking, setLatestBodyTracking] = useState(null);

  const trackingCustomerId = useMemo(
    () => customerDetails?.customer_id || customerDetails?.id,
    [customerDetails?.customer_id, customerDetails?.id]
  );

  const latestTrackingDate = useMemo(() => {
    if (!latestBodyTracking?.date) return null;
    try {
      return new Date(latestBodyTracking.date).toLocaleDateString('de-CH');
    } catch (error) {
      return null;
    }
  }, [latestBodyTracking?.date]);

  const latestTrackingStats = useMemo(() => {
    if (!latestBodyTracking) return [];
    const stats = [
      { key: 'weight', label: 'Gewicht', value: latestBodyTracking.weight, unit: 'kg' },
      { key: 'height', label: 'Grösse', value: latestBodyTracking.height, unit: 'cm' },
      { key: 'bmi', label: 'BMI', value: latestBodyTracking.bmi, unit: '' },
      { key: 'body_fat_percent', label: 'Körperfett', value: latestBodyTracking.body_fat_percent, unit: '%' },
      { key: 'muscle_mass', label: 'Muskelmasse', value: latestBodyTracking.muscle_mass, unit: 'kg' },
      { key: 'water_percent', label: 'Wasseranteil', value: latestBodyTracking.water_percent, unit: '%' },
      { key: 'visceral_fat', label: 'Viszeralfett', value: latestBodyTracking.visceral_fat, unit: '' },
      { key: 'metabolic_age', label: 'Stoffwechselalter', value: latestBodyTracking.metabolic_age, unit: 'J.' },
      { key: 'waist', label: 'Bauchumfang', value: latestBodyTracking.waist, unit: 'cm' }
    ];

    return stats.filter((item) => item.value !== null && item.value !== undefined && item.value !== '');
  }, [latestBodyTracking]);

  const handleTrackingDataChange = useCallback(() => {
    if (trackingCustomerId) {
      fetchLatestBodyTracking(trackingCustomerId);
    }
  }, [trackingCustomerId]);
  
  // NEW: InBody visibility setting
  const [inbodyVisible, setInbodyVisible] = useState(false);
  
  // NEW: Anamnese status for all customers
  const [anamneseStatus, setAnamneseStatus] = useState({});
  
  const navigate = useNavigate();

  // Fetch InBody visibility setting
  useEffect(() => {
    const fetchInbodySettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/inbody/settings/portal-visibility`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInbodyVisible(response.data.visible);
      } catch (error) {
        console.error('Error fetching inbody settings:', error);
        setInbodyVisible(false);
      }
    };
    fetchInbodySettings();
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchServices();
    fetchPointPackages();
    fetchDuplicateCount();
    fetchSubscriptionTemplates();
  }, []);

  // Deep-Link aus globaler Suche: /customers?open=<user_id>
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || customers.length === 0) return;
    const found = customers.find((c) => c.id === openId) || { id: openId };
    viewCustomerDetails(found);
    searchParams.delete('open');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, customers]);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchSubscriptionTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching subscription templates:', error);
      setSubscriptionTemplates([]);
    }
  };

  // NEW: Fetch latest body tracking data for a customer
  const fetchLatestBodyTracking = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/body-tracking/customer/${customerId}/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLatestBodyTracking(response.data);
    } catch (error) {
      console.error('Error fetching body tracking:', error);
      setLatestBodyTracking(null);
    }
  };

  useEffect(() => {
    if (searchTerm || activeFilter !== 'all') {
      let filtered = customers;
      
      // Apply active filter
      if (activeFilter === 'active') {
        filtered = filtered.filter(c => c.status === 'active');
      } else if (activeFilter === 'inactive') {
        filtered = filtered.filter(c => c.status !== 'active');
      }
      
      // Apply search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(customer =>
          customer.first_name.toLowerCase().includes(term) ||
          customer.last_name.toLowerCase().includes(term) ||
          customer.email.toLowerCase().includes(term) ||
          (customer.customer_number || '').toString().toLowerCase().includes(term)
        );
      }
      
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, activeFilter, customers]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Verwende erweiterten Endpoint mit Abo-Flag
      const response = await axios.get(`${API}/customers-with-subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomers(response.data || []);
      setFilteredCustomers(response.data || []);
      
      // Fetch anamnese status for all customers
      if (response.data && response.data.length > 0) {
        const customerIds = response.data.map(c => c.id);
        fetchAnamneseStatus(customerIds);
      }
    } catch (error) {
      console.error(error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch anamnese status for multiple customers
  const fetchAnamneseStatus = async (customerIds) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/anamnese/check-status`, customerIds, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnamneseStatus(response.data);
    } catch (error) {
      console.error('Error fetching anamnese status:', error);
    }
  };
  
  // Handle view anamnese - navigate to customer detail with anamnese tab
  const handleViewAnamnese = (customer) => {
    setSelectedCustomer(customer);
    setDetailDialogOpen(true);
    // Der Anamnese-Tab wird in der Detail-Ansicht angezeigt
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validierung Geschlecht
    if (!formData.gender) {
      toast.error('Bitte wählen Sie das Geschlecht aus');
      return;
    }
    
    try {
      // Phone already includes country code from PhoneInput
      const response = await axios.post(`${API}/customers/create-by-admin`, {
        ...formData,
        phone: formData.phone
      });
      toast.success('Kunde erstellt! Verifizierungs-E-Mail wurde gesendet.');
      toast.info(`Temporäres Passwort: ${response.data.temp_password}`);
      setDialogOpen(false);
      fetchCustomers();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      date_of_birth: '',
      gender: ''
    });
  };

  const viewCustomerDetails = async (customer) => {
    setSelectedCustomer(customer);
    setEditMode(false);
    setDeleteConfirmStep(0);
    setDeactivateConfirmStep(0); // Reset deactivate confirmation
    try {
      // Fetch customer by user_id
      const response = await axios.get(`${API}/customers/by-user/${customer.id}`);
      const details = response.data;
      setCustomerDetails(details);
      
      // Initialize edit form - now response.data contains all user fields too
      setEditFormData({
        customer_number: details.customer_number || '',
        first_name: details.first_name || '',
        last_name: details.last_name || '',
        email: details.email || '',
        phone: details.phone || '',
        date_of_birth: details.date_of_birth || '',
        gender: details.gender || '', // Geschlecht
        preferred_language: details.preferred_language || 'de', // Mehrsprachigkeit 06/2026
        street: details.street || '',
        postal_code: details.postal_code || '',
        city: details.city || '',
        emergency_contact_name: details.emergency_contact?.name || '',
        emergency_contact_phone: details.emergency_contact?.phone || '',
        height: details.height || '',
        suit_size: details.suit_size || '', // EMS Anzugsgrösse
        payment_timing: details.payment_timing || 'sofort',
        auto_charge_card: details.auto_charge_card !== false,
        individual_prices: details.individual_prices || {},
        individual_package_prices: details.individual_package_prices || {},
        individual_subscription_prices: details.individual_subscription_prices || {},
        discount_percent: details.discount_percent || '',
        points_discount_percent: (details.points_discount_percent ?? details.points_abo_discount_percent) || '',
        service_abo_discount_percent: (details.service_abo_discount_percent ?? details.points_abo_discount_percent) || '',
        email_reminders_enabled: details.email_reminders_enabled !== false,
        whatsapp_reminders_enabled: details.whatsapp_reminders_enabled !== false,
        email_newsletters_enabled: details.email_newsletters_enabled !== false,
        whatsapp_newsletters_enabled: details.whatsapp_newsletters_enabled !== false,
        appointment_changes_notifications: details.appointment_changes_notifications !== false,
        appointment_confirmation_notifications: details.appointment_confirmation_notifications !== false,
        // Separate Email/WhatsApp Felder - EXPLICIT false wenn nicht gesetzt
        confirmation_email: details.confirmation_email === true,
        confirmation_whatsapp: details.confirmation_whatsapp === true,
        changes_email: details.changes_email === true,
        changes_whatsapp: details.changes_whatsapp === true,
        // Unified Notification-Channel (Mobile-App "Bevorzugter Kanal")
        notification_channel: details.notification_channel || 'both',
        studio_id: details.studio_id || '',
      });

      // Reset tab-specific state to avoid stale data
      setPendingCharges(null);
      setRecurringSubscriptions([]);
      setCreditSubscriptions([]);
      setExhaustedSubscriptions([]);
      setPaymentTokens([]);
      setCustomerAppointments({ upcoming: [], past: [] });
      setPointTransactions([]);
      setPointsDetails(null);
      setLatestBodyTracking(null);
      setActiveSubscription(null);
      setShowAllPastAppointments(false);
      setShowAllUpcomingAppointments(false);
      setShowExhaustedAbos(false);

      setActiveDetailTab('overview');
      setDetailTabLoaded({
        account: false,
        payments: false,
        billing: false,
        tracking: false,
        anamnese: false,
        nutrition: false
      });
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Kundendetails');
      console.error(error);
    }
  };

  useEffect(() => {
    if (!detailDialogOpen || !customerDetails?.id) return;

    const customerId = customerDetails.id;

    if (activeDetailTab === 'account' && !detailTabLoaded.account) {
      setDetailTabLoaded(prev => ({ ...prev, account: true }));
      Promise.allSettled([
        fetchRecurringSubscriptions(customerId),
        fetchCreditSubscriptions(customerId),
        fetchCustomerAppointments(customerId),
        fetchPointTransactions(customerId),
        fetchPointsDetails(customerId),
        fetchPaymentTokens(customerId)
      ]).catch(() => null);
    }

    if (activeDetailTab === 'payments' && !detailTabLoaded.payments) {
      setDetailTabLoaded(prev => ({ ...prev, payments: true }));
      const promises = [];
      if (paymentTokens.length === 0) {
        promises.push(fetchPaymentTokens(customerId));
      }
      if (recurringSubscriptions.length === 0) {
        promises.push(fetchRecurringSubscriptions(customerId));
      }
      if (promises.length > 0) {
        Promise.allSettled(promises).catch(() => null);
      }
    }

    if (activeDetailTab === 'billing' && !detailTabLoaded.billing) {
      setDetailTabLoaded(prev => ({ ...prev, billing: true }));
      if (customerDetails.payment_timing === 'ende_monat') {
        fetchPendingCharges(customerId);
      }
    }

    if (activeDetailTab === 'tracking' && !detailTabLoaded.tracking) {
      setDetailTabLoaded(prev => ({ ...prev, tracking: true }));
      fetchLatestBodyTracking(customerId);
    }

    if (activeDetailTab === 'anamnese' && !detailTabLoaded.anamnese) {
      setDetailTabLoaded(prev => ({ ...prev, anamnese: true }));
    }

    if (activeDetailTab === 'nutrition' && !detailTabLoaded.nutrition) {
      setDetailTabLoaded(prev => ({ ...prev, nutrition: true }));
    }

    if (activeDetailTab === 'recovery' && !detailTabLoaded.recovery) {
      setDetailTabLoaded(prev => ({ ...prev, recovery: true }));
    }
  }, [activeDetailTab, detailDialogOpen, customerDetails?.id, customerDetails?.payment_timing, detailTabLoaded]);

  // Foto-Galerie laden, wenn der Fotos-Tab geöffnet wird
  useEffect(() => {
    if (!detailDialogOpen || activeDetailTab !== 'photos' || !customerDetails?.id) return;
    let cancelled = false;
    (async () => {
      setPhotosLoading(true);
      try {
        const res = await axios.get(`${API}/whatsapp/customer-photos/${customerDetails.id}`);
        if (!cancelled) setCustomerPhotos(res.data?.photos || []);
      } catch (e) {
        if (!cancelled) setCustomerPhotos([]);
      } finally {
        if (!cancelled) setPhotosLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeDetailTab, detailDialogOpen, customerDetails?.id]);

  const handleEditSubmit = async () => {
    try {
      console.log('Sending edit data:', editFormData);
      await axios.put(`${API}/customers/${customerDetails.id}/profile`, editFormData);
      toast.success('Kundendaten aktualisiert!');
      setEditMode(false);
      fetchCustomers();
      // Reload details - with null check
      if (selectedCustomer && selectedCustomer.id) {
        await viewCustomerDetails(selectedCustomer);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    }
  };

  // NEW: Fetch point packages
  const fetchPointPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/point-packages?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPointPackages(response.data);
    } catch (error) {
      console.error('Error fetching point packages:', error);
    }
  };

  const fetchPointTransactions = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/customers/${customerId}/point-transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPointTransactions(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setPointTransactions([]);
      return [];
    }
  };

  const fetchPointsDetails = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      setLoadingPointsDetails(true);
      const response = await axios.get(
        `${API}/customers/${customerId}/points-details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPointsDetails(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching points details:', error);
      setPointsDetails(null);
      return null;
    } finally {
      setLoadingPointsDetails(false);
    }
  };

  // NEW: Handle add points dialog open
  const handleOpenAddPointsDialog = () => {
    fetchPointPackages();
    setPointsFormData({
      package_id: '',
      custom_points: '',
      amount_chf: '',
      payment_method: 'rechnung_combo',
      notes: ''
    });
    setAddPointsDialogOpen(true);
  };

  // NEW: CHF-Gutschrift Dialog (Geschenkgutschein, Kulanz, Rückerstattung)
  // Triggert automation_rules.credit_added → Email + WhatsApp falls aktiviert
  const handleOpenAddCreditDialog = () => {
    setAddCreditFormData({ amount: '', reason: '', send_notification: true });
    setAddCreditDialogOpen(true);
  };

  const [addingCredit, setAddingCredit] = useState(false);
  const handleAddCredit = async () => {
    if (addingCredit) return;
    const amount = parseFloat(addCreditFormData.amount);
    const reason = (addCreditFormData.reason || '').trim();
    if (!amount || amount <= 0) {
      toast.error('Bitte einen gültigen Betrag eingeben');
      return;
    }
    if (!reason) {
      toast.error('Bitte einen Grund angeben');
      return;
    }
    setAddingCredit(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/customers/${customerDetails.id}/add-credit`,
        {
          amount,
          reason,
          send_notification: addCreditFormData.send_notification,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `CHF ${amount.toFixed(2)} gutgeschrieben. Neuer Saldo: CHF ${res.data.new_balance.toFixed(2)}` +
          (addCreditFormData.send_notification ? ' (Benachrichtigung wird gesendet)' : '')
      );
      setAddCreditDialogOpen(false);
      // Customer-Details neu laden, damit die neue Karte sofort sichtbar ist
      const updated = await axios.get(
        `${API}/customers/by-user/${customerDetails.user_id || customerDetails.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerDetails(updated.data);
    } catch (e) {
      console.error('add-credit error:', e);
      toast.error(e?.response?.data?.detail || 'Gutschrift fehlgeschlagen');
    } finally {
      setAddingCredit(false);
    }
  };

  // NEW: Handle add points
  const [addingPoints, setAddingPoints] = useState(false);
  const handleAddPoints = async () => {
    // Doppelklick-Schutz
    if (addingPoints) return;
    setAddingPoints(true);
    try {
      const token = localStorage.getItem('token');
      const selectedPackage = pointPackages.find(pkg => pkg.id === pointsFormData.package_id);
      
      const pointsToAdd = pointsFormData.custom_points 
        ? parseInt(pointsFormData.custom_points)
        : selectedPackage?.points_amount;

      const amountCHF = pointsFormData.amount_chf
        ? parseFloat(pointsFormData.amount_chf)
        : selectedPackage?.price;

      if (!pointsToAdd || pointsToAdd <= 0) {
        toast.error('Bitte geben Sie eine gültige Punktzahl ein');
        return;
      }

      // 07/2026: CHF 0 ist erlaubt (z.B. Gratis-Punkte) — aber Notiz Pflicht
      if (amountCHF === undefined || amountCHF === null || isNaN(amountCHF) || amountCHF < 0) {
        toast.error('Bitte geben Sie einen gültigen Betrag in CHF ein');
        return;
      }
      if (amountCHF === 0 && !(pointsFormData.notes || '').trim()) {
        toast.error('Bei CHF 0.00 bitte eine Notiz mit Begründung angeben');
        return;
      }

      await axios.post(
        `${API}/customers/${customerDetails.id}/add-points`,
        {
          points: pointsToAdd,
          amount_chf: amountCHF,
          payment_method: pointsFormData.payment_method,
          package_id: pointsFormData.package_id || null,
          notes: pointsFormData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${pointsToAdd} Punkte erfolgreich hinzugefügt!`);
      setAddPointsDialogOpen(false);
      // Reload customer details
      await viewCustomerDetails(selectedCustomer);
    } catch (error) {
      toast.error('Fehler beim Hinzufügen der Punkte');
      console.error(error);
    } finally {
      setAddingPoints(false);
    }
  };

  // NEW: Handle open history dialog
  const handleOpenHistoryDialog = async () => {
    try {
      await fetchPointTransactions(customerDetails.id);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Historie');
      console.error(error);
    }
  };

  // NEW: Fetch pending charges for end-of-month customers
  const fetchPendingCharges = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/customers/${customerId}/pending-charges`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingCharges(response.data);
    } catch (error) {
      console.error('Error fetching pending charges:', error);
      setPendingCharges(null);
    }
  };

  // 06/2026: Offenen Produkt-Posten (z.B. Shake) stornieren/entfernen
  const handleRemovePendingProduct = async (product) => {
    const customerId = customerDetails?.id || selectedCustomer?.customer_id;
    if (!product?.id || !customerId) return;
    const label = product.product_name || 'Produkt';
    if (!window.confirm(`„${label}" wirklich aus den offenen Posten entfernen?\n\nDer Posten wird storniert und nicht mehr in Rechnung gestellt.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/sales/${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`„${label}" entfernt`);
      await fetchPendingCharges(customerId);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Posten konnte nicht entfernt werden';
      toast.error(msg);
      console.error('Error removing pending product:', error);
    }
  };

  // NEW: Fetch customer appointments for Kontoübersicht
  const fetchCustomerAppointments = async (customerId) => {
    setLoadingAppointments(true);
    setShowAllPastAppointments(false);
    setShowAllUpcomingAppointments(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/appointments/with-details`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { customer_id: customerId }
        }
      );
      
      const customerApts = response.data || [];
      // Split into upcoming and past
      const now = new Date();
      const upcoming = customerApts
        .filter(apt => new Date(apt.start_time) >= now && apt.status !== 'cancelled')
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      
      const past = customerApts
        .filter(apt => new Date(apt.start_time) < now || apt.status === 'cancelled')
        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
      
      setCustomerAppointments({ upcoming, past });
    } catch (error) {
      console.error('Error fetching customer appointments:', error);
      setCustomerAppointments({ upcoming: [], past: [] });
    } finally {
      setLoadingAppointments(false);
    }
  };
  
  // Navigate to calendar on specific date
  const navigateToAppointment = (appointment) => {
    const date = new Date(appointment.start_time);
    const dateStr = date.toISOString().split('T')[0];
    setDetailDialogOpen(false);
    navigate(`/appointments?date=${dateStr}`);
  };
  
  // Cancel/Delete appointment from customer account
  const handleCancelAppointmentFromAccount = async (appointmentId) => {
    if (!window.confirm('Termin wirklich absagen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/appointments/${appointmentId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Termin abgesagt');
      // Refresh appointments
      if (customerDetails?.id) {
        await fetchCustomerAppointments(customerDetails.id);
      }
    } catch (error) {
      toast.error('Fehler beim Absagen');
      console.error(error);
    }
  };

  // NEW: Generate invoice for customer
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const handleGenerateInvoice = async () => {
    // Doppelklick-Schutz
    if (creatingInvoice) return;
    setCreatingInvoice(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/customers/${customerDetails.id}/generate-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Rechnung ${response.data.invoice_number} erfolgreich erstellt!`);
      setInvoiceDialogOpen(false);
      // Refresh pending charges
      await fetchPendingCharges(customerDetails.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Rechnung');
      console.error(error);
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Fetch ALL credit-based subscriptions (10er-Abos) for customer
  const [creditSubscriptions, setCreditSubscriptions] = useState([]);
  const [exhaustedSubscriptions, setExhaustedSubscriptions] = useState([]);
  const [showExhaustedAbos, setShowExhaustedAbos] = useState(false);
  
  // NEW: Subscription Transaction History
  const [subscriptionHistory, setSubscriptionHistory] = useState(null);
  const [showSubscriptionHistoryModal, setShowSubscriptionHistoryModal] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // NEU: State für Service-Bearbeitung
  const [editServicesDialogOpen, setEditServicesDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  
  const fetchSubscriptionTransactions = async (subscriptionId) => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      // Iter 06/2026 — User-Wunsch Markus: Timeline-Endpoint liefert
      // zusätzlich Rechnungen (welche bezahlt haben) + Trainer/Status
      // pro Termin. Vereinheitlicht 2 Sichten.
      const response = await axios.get(
        `${API}/subscriptions/${subscriptionId}/timeline`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscriptionHistory(response.data);
      setShowSubscriptionHistoryModal(true);
    } catch (error) {
      console.error('Error fetching subscription timeline:', error);
      toast.error('Fehler beim Laden der Abo-Übersicht');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const fetchCreditSubscriptions = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      // Hole alle Abos
      const response = await axios.get(
        `${API}/customers/${customerId}/subscriptions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const allSubs = response.data || [];
      
      // Setze ALLE Abos - Filterung erfolgt im Render
      setCreditSubscriptions(allSubs);
      
      // Hole auch abgelaufene/aufgebrauchte Abos für die Historie
      try {
        const historyResponse = await axios.get(
          `${API}/customers/${customerId}/subscriptions/history`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const historyData = historyResponse.data || [];
        
        // Abos mit 0 Credits aus der aktuellen Liste
        const exhaustedFromActive = allSubs.filter(s => s.credits_remaining <= 0);
        
        // Kombiniere: Abos aus History + Abos mit 0 Credits (ohne Duplikate)
        const historyIds = historyData.map(s => s.id);
        const uniqueExhausted = exhaustedFromActive.filter(s => !historyIds.includes(s.id));
        
        setExhaustedSubscriptions([...uniqueExhausted, ...historyData]);
      } catch (historyError) {
        console.error('Error fetching subscription history:', historyError);
        // Fallback: Nur Abos mit 0 Credits
        setExhaustedSubscriptions(allSubs.filter(s => s.credits_remaining <= 0));
      }
    } catch (error) {
      console.error('Error fetching credit subscriptions:', error);
      setCreditSubscriptions([]);
      setExhaustedSubscriptions([]);
    }
  };

  // NEW: Fetch ALL recurring subscriptions for customer
  const fetchRecurringSubscriptions = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/recurring-subscriptions/customers/${customerId}/recurring-subscriptions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const subs = response.data || [];
      setRecurringSubscriptions(subs);
      // Standardmässig das erste aktive Abo als "activeSubscription" setzen
      const firstActive = subs.find(s => s.status === 'active') || subs[0] || null;
      setActiveSubscription(firstActive);
    } catch (error) {
      console.error('Error fetching recurring subscriptions:', error);
      setRecurringSubscriptions([]);
      setActiveSubscription(null);
    }
  };

  // NEW: Create recurring subscription
  const handleCreateRecurringSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/recurring-subscriptions`,
        {
          customer_id: customerDetails.id,
          ...subscriptionFormData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Wiederkehrendes Punkte-Abo erfolgreich erstellt!');
      setSubscriptionDialogOpen(false);
      // Refresh subscriptions and customer details
      await fetchRecurringSubscriptions(customerDetails.id);
      await viewCustomerDetails(selectedCustomer);
      
      // Reset form
      setSubscriptionFormData({
        name: '',
        description: '',
        points_per_month: 100,
        price_per_month: 200,
        billing_cycle: 'monthly',
        payment_method: 'kreditkarte',
        allowed_service_ids: [],
        renewal_mode: 'monthly_25',
        low_balance_threshold: 2
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Abos');
      console.error(error);
    }
  };

  // NEW: Cancel recurring subscription
  const handleCancelRecurringSubscription = async () => {
    if (!window.confirm('Möchten Sie dieses Abo wirklich kündigen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/recurring-subscriptions/${activeSubscription.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Abo erfolgreich gekündigt!');
      // Refresh subscriptions
      await fetchRecurringSubscriptions(customerDetails.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Kündigen des Abos');
      console.error(error);
    }
  };

  // NEW: Update recurring subscription
  const handleUpdateRecurringSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/recurring-subscriptions/${activeSubscription.id}`,
        subscriptionFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Abo erfolgreich aktualisiert!');
      setSubscriptionDialogOpen(false);
      // Refresh subscriptions
      await fetchRecurringSubscriptions(customerDetails.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren des Abos');
      console.error(error);
    }
  };

  // NEW: Fetch payment tokens
  const fetchPaymentTokens = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/payrexx/customers/${customerId}/payment-tokens`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentTokens(response.data);
    } catch (error) {
      console.error('Error fetching payment tokens:', error);
      setPaymentTokens([]);
    }
  };

  // NEW: Create payment token gateway
  const handleCreatePaymentToken = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payrexx/customers/${customerDetails.id}/payment-tokens/create`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { gateway_id, payment_link } = response.data;
      
      // Store gateway ID and payment link
      setPendingGatewayId(gateway_id);
      setCardPaymentLink(payment_link);
      setAddCardDialogOpen(true);
      
      toast.info('Bitte öffnen Sie den Payment-Link, um Ihre Kreditkarte zu hinterlegen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Payment-Links');
      console.error(error);
    }
  };

  // NEW: Verify payment token
  const handleVerifyPaymentToken = async () => {
    if (!pendingGatewayId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payrexx/customers/${customerDetails.id}/payment-tokens/${pendingGatewayId}/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Kreditkarte erfolgreich hinterlegt!');
      setAddCardDialogOpen(false);
      setCardPaymentLink(null);
      setPendingGatewayId(null);
      
      // Refresh payment tokens
      await fetchPaymentTokens(customerDetails.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kreditkarte noch nicht autorisiert. Bitte versuchen Sie es erneut.');
      console.error(error);
    }
  };

  // NEW: Delete payment token
  const handleDeletePaymentToken = async (tokenId) => {
    if (!window.confirm('Möchten Sie diese Kreditkarte wirklich löschen?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/payrexx/customers/${customerDetails.id}/payment-tokens/${tokenId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Kreditkarte gelöscht');
      await fetchPaymentTokens(customerDetails.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen der Kreditkarte');
      console.error(error);
    }
  };

  // NEW: Save individual prices
  const handleSaveIndividualPrices = async () => {
    if (!customerDetails?.id) {
      toast.error('Kein Kunde ausgewählt – bitte Kundendetails erneut öffnen.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      
      // Clean up undefined values
      const cleanedPrices = {};
      Object.entries(editFormData.individual_prices || {}).forEach(([serviceId, price]) => {
        if (price !== undefined && price !== null && price !== '') {
          cleanedPrices[serviceId] = parseFloat(price);
        }
      });

      const cleanedPackagePrices = {};
      Object.entries(editFormData.individual_package_prices || {}).forEach(([packageId, price]) => {
        if (price !== undefined && price !== null && price !== '') {
          cleanedPackagePrices[packageId] = parseFloat(price);
        }
      });

      const cleanedSubscriptionPrices = {};
      Object.entries(editFormData.individual_subscription_prices || {}).forEach(([templateId, price]) => {
        if (price !== undefined && price !== null && price !== '') {
          cleanedSubscriptionPrices[templateId] = parseFloat(price);
        }
      });
      
      const res = await axios.put(
        `${API}/customers/${customerDetails.id}/profile`,
        { 
          individual_prices: cleanedPrices,
          individual_package_prices: cleanedPackagePrices,
          individual_subscription_prices: cleanedSubscriptionPrices,
          discount_percent: Math.min(parseFloat(editFormData.discount_percent) || 0, 100),
          points_discount_percent: Math.min(parseFloat(editFormData.points_discount_percent) || 0, 100),
          service_abo_discount_percent: Math.min(parseFloat(editFormData.service_abo_discount_percent) || 0, 100)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPricingDialogOpen(false);

      const jobId = res.data?.reprice_job_id;
      if (jobId) {
        // Schwere Neuberechnung läuft im Hintergrund — sofort weiterarbeiten.
        const cName = `${customerDetails.first_name || ''} ${customerDetails.last_name || ''}`.trim();
        setRepriceJob({ jobId, customerId: customerDetails.id, customerName: cName });
        toast.success('Preise gespeichert! Termine & offene Rechnungen werden im Hintergrund aktualisiert – du kannst weiterarbeiten.');
      } else {
        toast.success('Individuelle Preise gespeichert!');
      }
      
      // Refresh customer details
      await viewCustomerDetails(selectedCustomer);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Preise');
      console.error(error);
    }
  };

  // NEW: Add child to customer
  const handleAddChild = async () => {
    if (!childFormData.name || !childFormData.date_of_birth) {
      toast.error('Name und Geburtsdatum sind erforderlich');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/customers/${customerDetails.id}/children`,
        childFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Kind erfolgreich hinzugefügt!');
      setAddChildDialogOpen(false);
      setChildFormData({ name: '', date_of_birth: '' });
      
      // Refresh customer details
      await viewCustomerDetails(selectedCustomer);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen des Kindes');
      console.error(error);
    }
  };

  // NEW: Delete child from customer
  const handleDeleteChild = async (index) => {
    if (!window.confirm('Möchten Sie dieses Kind wirklich löschen?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/customers/${customerDetails.id}/children/${index}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Kind erfolgreich entfernt');
      
      // Refresh customer details
      await viewCustomerDetails(selectedCustomer);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Kindes');
      console.error(error);
    }
  };

  // 06/2026: App-Download-Link (Apple + Google Play) an den Kunden senden
  // (E-Mail + WhatsApp, in Kundensprache). Button im Profil-Header.
  const handleSendAppLink = async () => {
    const customerId = customerDetails?.id || selectedCustomer?.customer_id;
    if (!customerId) return;
    setSendingAppLink(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/customers/${customerId}/send-app-link`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const ch = (data?.channels || []).map((c) => (c === 'email' ? 'E-Mail' : 'WhatsApp')).join(' & ') || 'kein Kanal';
      toast.success(`App-Link gesendet (${ch})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'App-Link konnte nicht gesendet werden');
    } finally {
      setSendingAppLink(false);
    }
  };

  // NEW: Send payment link via both email and WhatsApp
  const handleSendPaymentLinkBoth = async () => {
    // selectedCustomer.id is the user_id; the Payrexx-Endpoint expects the
    // customer_id. Prefer customerDetails.id (loaded customer record) and
    // fall back to selectedCustomer.customer_id when available.
    const customerId = customerDetails?.id || selectedCustomer?.customer_id || selectedCustomer?.id;
    if (!customerId) return;

    const hasEmail = !!(customerDetails?.email || selectedCustomer?.email);
    const hasPhone = !!(customerDetails?.phone || selectedCustomer?.phone);

    if (!hasEmail && !hasPhone) {
      toast.error('Kunde hat weder Email noch Telefonnummer hinterlegt');
      return;
    }

    const token = localStorage.getItem('token');
    let sentMethods = [];
    let whatsappUrl = null;
    
    // Send Email
    if (hasEmail) {
      try {
        await axios.post(
          `${API}/payrexx/customers/${customerId}/payment-tokens/send-link?method=email`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        sentMethods.push('Email');
      } catch (error) {
        console.error('Email send failed:', error);
        toast.error(`Email-Versand fehlgeschlagen: ${error.response?.data?.detail || 'Unbekannter Fehler'}`);
      }
    }
    
    // Send WhatsApp
    if (hasPhone) {
      try {
        const response = await axios.post(
          `${API}/payrexx/customers/${customerId}/payment-tokens/send-link?method=whatsapp`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.manual_send) {
          // API-Versand fehlgeschlagen → manueller wa.me-Link als Fallback
          whatsappUrl = response.data.whatsapp_url;
          toast.info('WhatsApp-API nicht verfügbar — WhatsApp Web wird geöffnet, bitte Nachricht manuell senden.');
        } else {
          sentMethods.push('WhatsApp');
        }
      } catch (error) {
        console.error('WhatsApp send failed:', error);
        toast.error(`WhatsApp-Versand fehlgeschlagen: ${error.response?.data?.detail || 'Unbekannter Fehler'}`);
      }
    }
    
    // Show success message
    if (sentMethods.length > 0 || whatsappUrl) {
      if (sentMethods.length > 0) {
        toast.success(`Payment-Link wurde versendet per: ${sentMethods.join(' und ')}`);
      }
      // Open WhatsApp Web for manual fallback
      if (whatsappUrl) {
        window.open(whatsappUrl, '_blank');
      }
      
      setSendLinkDialogOpen(false);
    } else {
      toast.error('Versand fehlgeschlagen');
    }
  };

  // NEW: Send link (via CustomerManageLinks / dialogs)

  // NEW: Open subscription dialog
  const handleOpenSubscriptionDialog = async () => {
    // Ensure payment tokens are loaded
    if (paymentTokens.length === 0 && customerDetails?.id) {
      await fetchPaymentTokens(customerDetails.id);
    }

    if (paymentTokens.length === 0) {
      toast.error('Bitte hinterlegen Sie zuerst eine Kreditkarte für wiederkehrende Zahlungen');
      return;
    }

    // Standard: Formular leeren (Neues Abo)
    setSubscriptionFormData({
      name: '',
      description: '',
      points_per_month: 100,
      price_per_month: 200,
      billing_cycle: 'monthly',
      payment_method: 'kreditkarte', // Immer Kreditkarte für Abos
      allowed_service_ids: [],
      initial_price: null,
      initial_points: null,
      renewal_mode: 'monthly_25',
      low_balance_threshold: 2
    });

    setActiveSubscription(null); // Wir bearbeiten ein neues Abo
    setSubscriptionDialogOpen(true);
  };


  const handleDeleteConfirm = async () => {
    if (deleteConfirmStep < 2) {
      setDeleteConfirmStep(deleteConfirmStep + 1);
      return;
    }
    
    // 07/2026: Soft-Delete — Kunde landet 30 Tage in "Gelöschte Konten"
    // und wird per E-Mail über die Löschung benachrichtigt.
    const customerEmail = customerDetails?.email; // Capture vor Reset
    const customerName =
      [customerDetails?.first_name, customerDetails?.last_name]
        .filter(Boolean).join(' ') || 'Kunde';
    try {
      const res = await axios.delete(`${API}/customers/${customerDetails.id}`);
      setDetailDialogOpen(false);
      fetchCustomers();
      setDeleteConfirmStep(0);

      const mailInfo = res?.data?.confirmation_email_sent
        ? 'Der Kunde wurde per E-Mail benachrichtigt.'
        : 'Benachrichtigungs-Mail konnte nicht gesendet werden.';

      // E-Mail-Freigabe-Toast (User-Wunsch 09.05.2026): Admin sieht direkt
      // einen "Freigeben"-Button, damit die alte E-Mail-Adresse für eine
      // Neuregistrierung wieder verwendbar wird (purge-email Endpoint).
      if (customerEmail && !customerEmail.startsWith('deleted+')) {
        toast.success(`${customerName} gelöscht — 30 Tage wiederherstellbar unter System → Gelöschte Konten`, {
          duration: 12000,
          description: `${mailInfo} Soll die E-Mail "${customerEmail}" sofort für eine Neuregistrierung freigegeben werden?`,
          action: {
            label: 'E-Mail freigeben',
            onClick: async () => {
              try {
                await axios.post(`${API}/customers/purge-email`, {
                  email: customerEmail,
                  force: true,
                });
                toast.success(`E-Mail ${customerEmail} freigegeben`);
              } catch (err) {
                toast.error('Freigabe fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
              }
            },
          },
        });
      } else {
        toast.success('Kunde gelöscht — 30 Tage wiederherstellbar unter System → Gelöschte Konten');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleInviteIncomplete = async () => {
    if (invitingIncomplete) return;
    if (!window.confirm('Allen unvollständigen (inaktiven) Kunden eine Einladung zur Konto-Vervollständigung per E-Mail & WhatsApp senden?')) return;
    setInvitingIncomplete(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/customers/invite-incomplete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const n = res.data?.targeted ?? 0;
      if (n === 0) {
        toast.info('Keine unvollständigen Kunden gefunden.');
      } else {
        toast.success(`Einladung an ${n} unvollständige Kunden wird per E-Mail & WhatsApp versendet.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Einladungen konnten nicht gesendet werden.');
    } finally {
      setInvitingIncomplete(false);
    }
  };


  const handleRepairMerges = async () => {
    if (repairingMerges) return;
    if (!window.confirm('Bereits zusammengeführte Kunden reparieren? Termine, die im Kalender blieben, aber im Profil verschwunden sind, werden auf den Haupt-Datensatz umgehängt. Es wird nichts gelöscht.')) return;
    setRepairingMerges(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/customers/merge/repair`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const n = res.data?.repaired_pairs ?? 0;
      if (n === 0) {
        toast.info('Nichts zu reparieren – alle zusammengeführten Kunden sind sauber.');
      } else {
        toast.success(`${n} zusammengeführte Kunde(n) repariert – Termine/Zahlungen wieder korrekt zugeordnet.`);
        try { fetchCustomers(); } catch (_) { /* noop */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reparatur fehlgeschlagen.');
    } finally {
      setRepairingMerges(false);
    }
  };

  const handleSyncFromUsers = async () => {
    if (syncingUsers) return;
    setSyncingUsers(true);
    try {
      const token = localStorage.getItem('token');
      // 1) Vorschau
      const prev = await axios.post(`${API}/admin/customers/sync-from-users?dry_run=true`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const n = prev.data?.customers_updated ?? 0;
      if (n === 0) {
        toast.info('Alle Kundendaten sind vollständig – nichts zu ergänzen.');
        setSyncingUsers(false);
        return;
      }
      const pf = prev.data?.per_field || {};
      const detail = `Name: ${pf.first_name || 0}, E-Mail: ${pf.email || 0}, Telefon: ${pf.phone || 0}, Geburtsdatum: ${pf.date_of_birth || 0}`;
      if (!window.confirm(`${n} Kunde(n) haben Daten nur im Login-Konto. Jetzt in den Kundendatensatz ergänzen?\n\nEs werden nur LEERE Felder befüllt (nichts überschrieben).\n\nErgänzt: ${detail}`)) {
        setSyncingUsers(false);
        return;
      }
      // 2) Ausführen
      const res = await axios.post(`${API}/admin/customers/sync-from-users`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${res.data?.customers_updated ?? 0} Kunde(n) ergänzt (Name/E-Mail/Telefon/Geburtsdatum aus Login-Konto).`);
      try { fetchCustomers(); } catch (_) { /* noop */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ergänzen fehlgeschlagen.');
    } finally {
      setSyncingUsers(false);
    }
  };


  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-y-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users size={32} />
            Kunden
          </h1>
          <p className="text-gray-600 mt-1">Verwalten Sie Ihre Kunden</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleInviteIncomplete}
            disabled={invitingIncomplete}
            data-testid="invite-incomplete-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            {invitingIncomplete ? 'Wird gesendet…' : 'Unvollständige einladen'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setCardCampaignOpen(true)}
            data-testid="card-campaign-btn"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Karten-Link
          </Button>
          <Button
            variant="outline"
            onClick={() => setMergeDialogOpen(true)}
            data-testid="merge-customers-btn"
          >
            <Merge className="h-4 w-4 mr-2" />
            Zusammenführen
          </Button>
          <CreateCustomerDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
          />
          <OnboardingInviteDialog onCreated={() => fetchCustomers && fetchCustomers()} />
        </div>
      </div>

      {/* Datenpflege & Werkzeuge (aufklappbar) — Admin-Werkzeuge gebündelt:
          Doppelte Kunden, Zusammengeführte reparieren, Telefonnummern bereinigen. */}
      <Card data-testid="data-tools-card">
        <button
          type="button"
          onClick={() => setToolsOpen((o) => !o)}
          className="w-full flex items-center justify-between px-6 py-4 text-left"
          data-testid="data-tools-toggle"
        >
          <span className="flex items-center gap-2 font-semibold text-gray-900">
            <Wrench className="h-5 w-5 text-teal-600" />
            Datenpflege &amp; Werkzeuge
            {duplicateCount ? (
              <Badge variant="secondary" className="ml-1">{duplicateCount} Duplikate</Badge>
            ) : null}
          </span>
          {toolsOpen ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
        </button>
        {toolsOpen && (
          <CardContent className="border-t pt-6 space-y-6" data-testid="data-tools-content">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDuplicates(true)}
                data-testid="open-duplicates-btn"
                title="Zeigt wahrscheinliche Doppel-Logins zum Zusammenführen"
              >
                <Users className="h-4 w-4 mr-2" />
                Doppelte Kunden{duplicateCount ? ` (${duplicateCount})` : ''}
              </Button>
              <Button
                variant="outline"
                onClick={handleRepairMerges}
                disabled={repairingMerges}
                data-testid="repair-merges-btn"
                title="Behebt Termine/Zahlungen, die nach einem Kunden-Zusammenführen falsch zugeordnet blieben (im Kalender sichtbar, im Profil weg)"
              >
                <Wrench className="h-4 w-4 mr-2" />
                {repairingMerges ? 'Wird repariert…' : 'Zusammengeführte reparieren'}
              </Button>
              <Button
                variant="outline"
                onClick={handleSyncFromUsers}
                disabled={syncingUsers}
                data-testid="sync-from-users-btn"
                title="Ergänzt fehlende Kundendaten (Name, E-Mail, Telefon, Geburtsdatum) aus dem verknüpften Login-Konto. Nur leere Felder werden befüllt."
              >
                <Users className="h-4 w-4 mr-2" />
                {syncingUsers ? 'Wird ergänzt…' : 'Kundendaten ergänzen'}
              </Button>
            </div>
            <div className="border-t pt-6">
              <PhoneCleanupCard embedded />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Customer List Table */}
      <CustomerListTable
        customers={customers}
        filteredCustomers={filteredCustomers}
        loading={loading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        onViewCustomer={viewCustomerDetails}
        anamneseStatus={anamneseStatus}
        onViewAnamnese={handleViewAnamnese}
      />

      {/* Customer Detail Modal */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0">
          <DialogHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="customer-detail-header">
              <DialogTitle>Kundendetails</DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendAppLink}
                  disabled={!customerDetails?.id || sendingAppLink}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="customer-send-app-link-btn"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  {sendingAppLink ? 'Wird gesendet…' : 'App senden'}
                </Button>
                <QuickExportMenu
                  customerId={customerDetails?.id}
                  customerName={`${customerDetails?.first_name || ''} ${customerDetails?.last_name || ''}`.trim()}
                  disabled={!customerDetails?.id}
                />
              </div>
            </div>
          </DialogHeader>
          
          {/* Tabs for better organization.
              min-w-0: verhindert dass die breite Tab-Leiste (min-w-max im
              Scroll-Wrapper) als Grid-Item den ganzen Dialog horizontal
              aufbläht — Inhalte liefen sonst rechts aus dem Modal (06/2026). */}
          <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab} className="w-full min-w-0" data-testid="customer-detail-tabs">
            {/* 08/2026 — Tab-Redesign: gleichmässiges Grid statt gequetschter
                Flex-Wrap-Zeile; Lucide-Icons statt Emojis; klare Aktiv-Marke. */}
            <TabsList
              className="grid w-full h-auto grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-1.5 bg-muted/60 p-1.5 rounded-xl mb-3"
              data-testid="customer-detail-tabs-list"
            >
              {[
                { value: 'overview', label: 'Profil', Icon: User, testid: 'customer-detail-tab-overview' },
                { value: 'anamnese', label: 'Anamnese', Icon: ClipboardList, testid: 'customer-detail-tab-anamnese' },
                { value: 'fields', label: 'Felder', Icon: ListChecks, testid: 'customer-detail-tab-fields' },
                { value: 'notes', label: 'Notizen', Icon: StickyNote, testid: 'customer-detail-tab-notes' },
                { value: 'tracking', label: 'Körper', Icon: Scale, testid: 'customer-detail-tab-tracking' },
                { value: 'recovery', label: 'Recovery', Icon: HeartPulse, testid: 'customer-detail-tab-recovery' },
                { value: 'nutrition', label: 'Ernährung', Icon: Apple, testid: 'customer-detail-tab-nutrition' },
                { value: 'relationships', label: 'Beziehung', Icon: Users, testid: 'customer-detail-tab-relationships' },
                { value: 'photos', label: 'Fotos', Icon: ImageIcon, testid: 'customer-detail-tab-photos' },
                { value: 'payments', label: 'Zahlung', Icon: CreditCard, testid: 'customer-detail-tab-payments' },
                { value: 'account', label: 'Konto', Icon: Wallet, testid: 'customer-detail-tab-account' },
                { value: 'transactions', label: 'Historie', Icon: History, testid: 'customer-detail-tab-transactions' },
                { value: 'billing', label: 'Offene Trainings', Icon: Receipt, testid: 'customer-detail-tab-billing' },
                { value: 'series', label: 'Serien', Icon: Repeat, testid: 'customer-detail-tab-series' },
                { value: 'tarif595', label: 'T595', Icon: FileText, testid: 'customer-detail-tab-tarif595' },
                { value: 'settings', label: 'Einstellungen', Icon: Settings, testid: 'customer-detail-tab-settings' },
              ].map(({ value, label, Icon, testid }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  data-testid={testid}
                  className="flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium rounded-lg whitespace-nowrap transition-colors data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground hover:text-foreground"
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            
            {customerDetails && selectedCustomer && (
              <>
                {/* Account Tab - Extracted Component */}
                <TabsContent value="account" className="space-y-4">
                  <CustomerAccountTab
                    customerDetails={customerDetails}
                    pointsDetails={pointsDetails}
                    recurringSubscriptions={recurringSubscriptions}
                    creditSubscriptions={creditSubscriptions}
                    exhaustedSubscriptions={exhaustedSubscriptions}
                    services={services}
                    activeSubscription={activeSubscription}
                    customerAppointments={customerAppointments}
                    pointTransactions={pointTransactions}
                    loadingAppointments={loadingAppointments}
                    loadingPointsDetails={loadingPointsDetails}
                    showAllUpcomingAppointments={showAllUpcomingAppointments}
                    showAllPastAppointments={showAllPastAppointments}
                    showExhaustedAbos={showExhaustedAbos}
                    selectedCustomer={selectedCustomer}
                    API={API}
                    handleOpenAddPointsDialog={handleOpenAddPointsDialog}
                    handleOpenAddCreditDialog={handleOpenAddCreditDialog}
                    handleOpenHistoryDialog={handleOpenHistoryDialog}
                    handleOpenSubscriptionDialog={handleOpenSubscriptionDialog}
                    fetchSubscriptionTransactions={fetchSubscriptionTransactions}
                    fetchCreditSubscriptions={fetchCreditSubscriptions}
                    fetchPointsDetails={fetchPointsDetails}
                    navigateToAppointment={navigateToAppointment}
                    handleCancelAppointmentFromAccount={handleCancelAppointmentFromAccount}
                    viewCustomerDetails={viewCustomerDetails}
                    setEditingPackage={setEditingPackage}
                    setSelectedServiceIds={setSelectedServiceIds}
                    setEditServicesDialogOpen={setEditServicesDialogOpen}
                    setShowSubscriptionHistoryModal={setShowSubscriptionHistoryModal}
                    setSubscriptionHistory={setSubscriptionHistory}
                    setLoadingHistory={setLoadingHistory}
                    setShowAllUpcomingAppointments={setShowAllUpcomingAppointments}
                    setShowAllPastAppointments={setShowAllPastAppointments}
                    setShowExhaustedAbos={setShowExhaustedAbos}
                    toast={toast}
                    axios={axios}
                  />
                </TabsContent>

                {/* Anamnese Tab */}
                <TabsContent value="anamnese" className="space-y-4">
                  {(activeDetailTab === 'anamnese' || detailTabLoaded.anamnese) && (
                    <CustomerAnamnese 
                      customerId={customerDetails?.customer_id || customerDetails?.id}
                      customerName={`${customerDetails?.first_name || ''} ${customerDetails?.last_name || ''}`.trim()}
                      onSave={() => fetchAnamneseStatus(customers.map(c => c.id))}
                    />
                  )}
                </TabsContent>

                {/* Eigene Felder Tab */}
                <TabsContent value="fields" className="space-y-4">
                  {activeDetailTab === 'fields' && (
                    <CustomerCustomFieldsTab customerId={customerDetails?.customer_id || customerDetails?.id} />
                  )}
                </TabsContent>

                {/* Notizen Tab */}
                <TabsContent value="notes" className="space-y-4">
                  {activeDetailTab === 'notes' && (
                    <CustomerNotesTab customerId={customerDetails?.customer_id || customerDetails?.id} />
                  )}
                </TabsContent>

                {/* Body Tracking Tab - Kombiniert mit InBody */}
                <TabsContent value="tracking" className="space-y-6">
                  {/* Letzte Körperdaten Summary */}
                  {latestBodyTracking && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="tracking-latest-summary">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h4 className="font-semibold text-blue-900">⚖️ Aktuelle Körperdaten</h4>
                          {latestTrackingDate && (
                            <p className="text-sm text-blue-700" data-testid="tracking-latest-date">
                              Letzte Messung: {latestTrackingDate}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tracking-latest-stats">
                        {latestTrackingStats.map((stat) => (
                          <div key={stat.key} className="bg-white p-3 rounded border border-blue-200" data-testid={`tracking-latest-${stat.key}`}>
                            <p className="text-xs text-gray-500">{stat.label}</p>
                            <p className="text-lg font-bold text-blue-900">
                              {stat.value}{stat.unit ? ` ${stat.unit}` : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Manuelle Körpermessungen */}
                  <div data-testid="tracking-manual-section">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">📏 Manuelle Messungen</h3>
                    <p className="text-sm text-gray-500 mb-4">Gewicht, Körperfett, Muskelmasse und weitere Körpermasse manuell erfassen und verfolgen.</p>
                    <CustomerBodyTracking 
                      customerId={trackingCustomerId}
                      customerName={`${customerDetails?.first_name || ''} ${customerDetails?.last_name || ''}`.trim()}
                      onDataChange={handleTrackingDataChange}
                    />
                  </div>
                  
                  {/* InBody Körperanalyse - NUR wenn aktiviert */}
                  {inbodyVisible && (
                    <div className="border-t border-gray-200 pt-6" data-testid="tracking-inbody-section">
                      <h3 className="text-lg font-semibold mb-2 text-gray-700">📊 InBody Analyse</h3>
                      <p className="text-sm text-gray-500 mb-4">
                        Detaillierte Körperanalyse vom InBody-Gerät. Zeigt Muskelmasse, Körperfett, 
                        Körperwasser und weitere professionelle Messwerte.
                      </p>
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        {customerDetails?.id ? (
                          <CustomerInBodyData 
                            customerId={customerDetails?.id}
                            isPortal={false}
                          />
                        ) : (
                          <div className="text-gray-500 text-center py-4">Lade Kundendaten...</div>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Recovery Tab */}
                <TabsContent value="recovery" className="space-y-4">
                  {(activeDetailTab === 'recovery' || detailTabLoaded.recovery) && customerDetails?.id && (
                    <CustomerRecoveryTab customerId={customerDetails.id} />
                  )}
                </TabsContent>

                {/* Nutrition Tab */}
                <TabsContent value="nutrition" className="space-y-4">
                  {(activeDetailTab === 'nutrition' || detailTabLoaded.nutrition) && (
                    <>
                      {/* Prozentbasierte Ernährungsziele */}
                      <div className="bg-gray-50 rounded-lg p-4 border">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-2xl">🎯</span>
                          <div>
                            <h3 className="font-semibold text-lg">Ernährungsziele</h3>
                            <p className="text-sm text-gray-500">Kalorien, Makros (prozentbasiert) & Mahlzeiten-Verteilung</p>
                          </div>
                        </div>
                        
                        <NutritionGoalsEditor
                          customerId={customerDetails?.id}
                          token={localStorage.getItem('token')}
                          apiUrl={API}
                          isAdmin={true}
                          compact={false}
                          onSave={(savedGoals) => {
                            // Update local state with saved goals
                            setCustomerDetails(prev => ({
                              ...prev,
                              daily_calorie_goal: savedGoals.daily_calorie_goal,
                              meal_distribution: savedGoals.meal_distribution,
                              protein_percent: savedGoals.protein_percent,
                              carbs_percent: savedGoals.carbs_percent,
                              fat_percent: savedGoals.fat_percent
                            }));
                          }}
                        />
                      </div>
                      
                      {/* Individuelle Nachbrenn-Einstellungen */}
                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                        <div className="flex items-center gap-2 mb-4">
                          <Zap className="w-6 h-6 text-orange-500" />
                          <div>
                            <h3 className="font-semibold text-lg text-orange-900">Nachbrenn-Einstellungen (EPOC)</h3>
                            <p className="text-sm text-orange-700">Individuelle Nachbrenn-Prozentsätze pro Trainingsart</p>
                          </div>
                        </div>
                        
                        <CustomerAfterburnSettings
                          customerId={customerDetails?.id}
                          customerName={`${customerDetails?.first_name || ''} ${customerDetails?.last_name || ''}`.trim()}
                        />
                      </div>
                      
                      <CustomerNutrition 
                        customerId={customerDetails?.customer_id || customerDetails?.id}
                        customerName={`${customerDetails?.first_name || ''} ${customerDetails?.last_name || ''}`.trim()}
                      />
                    </>
                  )}
                </TabsContent>

                {/* Profile/Overview Tab */}
                {/* Overview Tab - Extracted Component */}
                <TabsContent value="overview" className="space-y-6">
                  <CustomerOverviewTab
                    customerDetails={customerDetails}
                    setCustomerDetails={setCustomerDetails}
                    selectedCustomer={selectedCustomer}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    editFormData={editFormData}
                    setEditFormData={setEditFormData}
                    studios={studios}
                    deleteConfirmStep={deleteConfirmStep}
                    setDeleteConfirmStep={setDeleteConfirmStep}
                    deactivateConfirmStep={deactivateConfirmStep}
                    setAddChildDialogOpen={setAddChildDialogOpen}
                    setChildFormData={setChildFormData}
                    handleDeleteChild={handleDeleteChild}
                    handleEditSubmit={handleEditSubmit}
                    handleDeleteConfirm={handleDeleteConfirm}
                  />
                </TabsContent>

                {/* Relationships Tab - NEW */}
                <TabsContent value="relationships" className="space-y-4">
                  <PaymentRelationships 
                    customerId={customerDetails.id} 
                    customerName={`${selectedCustomer.first_name} ${selectedCustomer.last_name}`}
                  />
                </TabsContent>

                {/* Transactions Tab */}
                <TabsContent value="transactions" className="space-y-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Übersicht aller Zahlungen und Trainings, gruppiert nach Monat. Zeigt klare Verbindung zwischen Zahlung und Training.
                  </p>
                  <ComprehensiveTransactions customerId={customerDetails.id} />
                </TabsContent>


                {/* Payments Tab - Extracted Component */}
                <TabsContent value="payments" className="space-y-6">
                  <CustomerPaymentsTab
                    paymentTokens={paymentTokens}
                    handleCreatePaymentToken={handleCreatePaymentToken}
                    setSendLinkDialogOpen={setSendLinkDialogOpen}
                    handleDeletePaymentToken={handleDeletePaymentToken}
                    customerId={customerDetails?.id || selectedCustomer?.customer_id}
                  />
                </TabsContent>

                {/* Billing Tab - Extracted Component */}
                <TabsContent value="billing" className="space-y-6">
                  <CustomerBillingTab
                    editFormData={editFormData}
                    pendingCharges={pendingCharges}
                    setInvoiceDialogOpen={setInvoiceDialogOpen}
                    onRemoveProduct={handleRemovePendingProduct}
                  />
                </TabsContent>

                {/* Terminserien Tab (08/2026) */}
                <TabsContent value="series" className="space-y-6">
                  <CustomerSeriesTab customerId={customerDetails?.id} />
                </TabsContent>

                {/* Tarif 595 Versicherungsbelege */}
                <TabsContent value="tarif595" className="space-y-6">
                  <CustomerTarif595Tab
                    customerId={customerDetails?.id}
                    customerName={`${customerDetails?.first_name || ''} ${customerDetails?.last_name || ''}`.trim()}
                    token={localStorage.getItem('token')}
                  />
                </TabsContent>

                {/* Settings Tab - Extracted Component */}
                <TabsContent value="photos" className="space-y-4">
                  {photosLoading ? (
                    <div className="text-center py-8 text-muted-foreground" data-testid="customer-photos-loading">Fotos werden geladen…</div>
                  ) : customerPhotos.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground" data-testid="customer-photos-empty">
                      <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p>Noch keine Fotos mit diesem Kunden ausgetauscht.</p>
                      <p className="text-xs mt-1">Fotos aus dem WhatsApp-Chat erscheinen hier automatisch.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="customer-photos-grid">
                      {customerPhotos.map((p) => {
                        const src = String(p.media_url).startsWith('http') ? p.media_url : `${process.env.REACT_APP_BACKEND_URL}${p.media_url}`;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setLightboxPhoto(src)}
                            className="relative aspect-square rounded-lg overflow-hidden border hover:ring-2 hover:ring-teal-500 transition-shadow"
                            data-testid={`customer-photo-${p.id}`}
                          >
                            <img src={src} alt="Foto" className="w-full h-full object-cover" />
                            <span className={`absolute bottom-1 left-1 text-[9px] px-1 rounded ${p.is_from_customer ? 'bg-teal-600 text-white' : 'bg-zinc-800/80 text-white'}`}>
                              {p.is_from_customer ? 'Kunde' : 'Wir'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="settings" className="space-y-6">
                  <CustomerSettingsTab
                    customerDetails={customerDetails}
                    setCustomerDetails={setCustomerDetails}
                    statusChangeLoading={statusChangeLoading}
                    setStatusChangeLoading={setStatusChangeLoading}
                    setCustomers={setCustomers}
                    setDetailDialogOpen={setDetailDialogOpen}
                    setSelectedCustomer={setSelectedCustomer}
                    editFormData={editFormData}
                    setEditFormData={setEditFormData}
                    services={services}
                    pointPackages={pointPackages}
                    subscriptionTemplates={subscriptionTemplates}
                    setPricingDialogOpen={setPricingDialogOpen}
                    pendingCharges={pendingCharges}
                    setInvoiceDialogOpen={setInvoiceDialogOpen}
                  />
                </TabsContent>
              </>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* NEW: Points History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Punktehistorie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {pointTransactions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Keine Transaktionen vorhanden</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {pointTransactions.map((transaction) => {
                  const isPositive = transaction.points_amount > 0;
                  const typeIcon = {
                    purchase: '💰',
                    booking: '📅',
                    refund: '↩️',
                    no_show_penalty: '❌'
                  }[transaction.transaction_type] || '📝';
                  
                  const typeLabel = {
                    purchase: 'Kauf',
                    booking: 'Buchung',
                    refund: 'Rückerstattung',
                    no_show_penalty: 'No-Show'
                  }[transaction.transaction_type] || transaction.transaction_type;

                  return (
                    <div key={transaction.id} className="p-3 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{typeIcon}</span>
                          <div>
                            <p className="font-medium">{typeLabel}</p>
                            <p className="text-sm text-gray-600">{transaction.description}</p>
                            {transaction.subscription_id && (
                              <p className="text-xs text-blue-600 font-medium mt-1">
                                🎫 Abgebucht von Abo
                              </p>
                            )}
                            {transaction.bonus_points > 0 && (
                              <p className="text-xs text-amber-600 font-medium mt-1" data-testid="admin-bonus-points-badge">
                                🎁 davon {transaction.bonus_points} Bonus-Punkte geschenkt
                              </p>
                            )}
                            <p className="text-xs text-gray-400">
                              {new Date(transaction.created_at).toLocaleString('de-CH')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${
                            isPositive ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {isPositive ? '+' : ''}{transaction.points_amount} Punkte
                          </p>
                          <p className="text-sm text-gray-500">
                            Saldo: {transaction.balance_after}
                          </p>
                        </div>
                        {!transaction.invoice_id && transaction.transaction_type === 'purchase' && !transaction.reversed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm('Diese Punktebuchung wirklich rückgängig machen?')) return;
                              try {
                                const token = localStorage.getItem('token');
                                await axios.post(
                                  `${API}/customers/${customerDetails.id}/point-transactions/${transaction.id}/reverse`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                toast.success('Punktebuchung wurde rückgängig gemacht');
                                viewCustomerDetails(selectedCustomer);
                              } catch (error) {
                                console.error('Error reversing transaction', error);
                                const msg = error.response?.data?.detail || 'Fehler beim Rückgängig machen';
                                toast.error(msg);
                              }
                            }}
                          >
                            Rückgängig
                          </Button>
                        )}

                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              Schliessen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Add Points Dialog */}
      <Dialog open={addPointsDialogOpen} onOpenChange={setAddPointsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Punkte hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Punktepaket auswählen (optional)</Label>
              <Select
                value={pointsFormData.package_id || 'custom'}
                onValueChange={(value) => setPointsFormData({...pointsFormData, package_id: value === 'custom' ? '' : value, custom_points: ''})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Paket auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Benutzerdefiniert</SelectItem>
                  {pointPackages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id}>
                      {pkg.name} - {pkg.points_amount} Punkte (CHF {pkg.price.toFixed(2)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(!pointsFormData.package_id || pointsFormData.package_id === 'custom') && (
              <>
                <div>
                  <Label>Betrag in CHF</Label>
                  <Input
                    type="number"
                    step="0.05"
                    min="0"
                    value={pointsFormData.amount_chf}
                    onChange={(e) => setPointsFormData({ ...pointsFormData, amount_chf: e.target.value })}
                    placeholder="z.B. 120.00"
                  />
                </div>

                <div>
                  <Label>Benutzerdefinierte Punktzahl</Label>
                  <Input
                    type="number"
                    min="1"
                    value={pointsFormData.custom_points}
                    onChange={(e) => setPointsFormData({...pointsFormData, custom_points: e.target.value})}
                    placeholder="Anzahl Punkte"
                  />
                </div>
              </>
            )}

            <div>
              <Label>Zahlungsmethode</Label>
              <Select
                value={pointsFormData.payment_method}
                onValueChange={(value) => setPointsFormData({...pointsFormData, payment_method: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="rechnung_combo">Rechnung / TWINT senden</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notiz (optional)</Label>
              <Input
                value={pointsFormData.notes}
                onChange={(e) => setPointsFormData({...pointsFormData, notes: e.target.value})}
                placeholder="z.B. Rechnungsnummer, Bemerkung"
              />
            </div>

            {pointsFormData.package_id && pointsFormData.package_id !== 'custom' && (
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-sm font-medium text-teal-900">
                  Ausgewähltes Paket: {pointPackages.find(p => p.id === pointsFormData.package_id)?.name}
                </p>
                <p className="text-xs text-teal-700 mt-1">
                  {pointPackages.find(p => p.id === pointsFormData.package_id)?.points_amount} Punkte für CHF {pointPackages.find(p => p.id === pointsFormData.package_id)?.price.toFixed(2)}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setAddPointsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAddPoints} disabled={addingPoints} data-testid="add-points-submit-btn">
              {addingPoints ? '⏳ Wird gebucht…' : 'Punkte hinzufügen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CHF-Gutschrift Dialog */}
      <Dialog open={addCreditDialogOpen} onOpenChange={setAddCreditDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-credit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>💰</span> CHF-Guthaben gutschreiben
            </DialogTitle>
            <DialogDescription>
              Guthaben wird beim nächsten Kauf automatisch verrechnet. Beispiele:
              Geschenkgutschein, Kulanz-Gutschrift, Rückerstattung.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="credit-amount">Betrag (CHF)</Label>
              <Input
                id="credit-amount"
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                placeholder="z.B. 20.00"
                value={addCreditFormData.amount}
                onChange={(e) =>
                  setAddCreditFormData({ ...addCreditFormData, amount: e.target.value })
                }
                data-testid="add-credit-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="credit-reason">Grund (wird dem Kunden mitgeteilt)</Label>
              <Input
                id="credit-reason"
                type="text"
                placeholder="z.B. Geschenkgutschein 50. Geburtstag"
                value={addCreditFormData.reason}
                onChange={(e) =>
                  setAddCreditFormData({ ...addCreditFormData, reason: e.target.value })
                }
                data-testid="add-credit-reason-input"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={addCreditFormData.send_notification}
                onChange={(e) =>
                  setAddCreditFormData({
                    ...addCreditFormData,
                    send_notification: e.target.checked,
                  })
                }
                className="mt-0.5"
                data-testid="add-credit-notify-checkbox"
              />
              <span className="text-gray-700">
                Email + WhatsApp an Kunde senden
                <span className="block text-xs text-gray-500 mt-0.5">
                  Verwendet die Vorlage <strong>credit_added</strong> aus
                  Kommunikation → Automatisierung. Kann dort komplett angepasst
                  oder deaktiviert werden.
                </span>
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAddCreditDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleAddCredit}
              disabled={addingCredit}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-credit-confirm-btn"
            >
              {addingCredit ? '⏳ Wird gebucht…' : 'Gutschrift bestätigen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Generation Confirmation Dialog - Extracted Component */}
      <InvoiceDialog
        open={invoiceDialogOpen}
        onOpenChange={setInvoiceDialogOpen}
        pendingCharges={pendingCharges}
        onGenerateInvoice={handleGenerateInvoice}
        generating={creatingInvoice}
      />

      {/* Individual Pricing Dialog - Extracted Component */}
      <IndividualPricingDialog
        open={pricingDialogOpen}
        onOpenChange={setPricingDialogOpen}
        services={services}
        pointPackages={pointPackages}
        subscriptionTemplates={subscriptionTemplates}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        onSavePrices={handleSaveIndividualPrices}
      />

      {/* Preis-Hintergrundlauf Fortschritt (08/2026) */}
      {repriceJob && (
        <RepriceProgress
          customerId={repriceJob.customerId}
          jobId={repriceJob.jobId}
          customerName={repriceJob.customerName}
          onDone={() => setRepriceJob(null)}
        />
      )}

      <SendPaymentLinkDialog
        open={sendLinkDialogOpen}
        onOpenChange={setSendLinkDialogOpen}
        selectedCustomer={selectedCustomer}
        onSendPaymentLink={handleSendPaymentLinkBoth}
      />

      {/* Add Credit Card Dialog - Extracted Component */}
      <AddCardDialog
        open={addCardDialogOpen}
        onOpenChange={setAddCardDialogOpen}
        cardPaymentLink={cardPaymentLink}
        onVerifyPaymentToken={handleVerifyPaymentToken}
      />

      {/* Recurring Subscription Dialog - Extracted Component */}
      <RecurringSubscriptionDialog
        open={subscriptionDialogOpen}
        onOpenChange={setSubscriptionDialogOpen}
        recurringSubscriptions={recurringSubscriptions}
        activeSubscription={activeSubscription}
        setActiveSubscription={setActiveSubscription}
        subscriptionFormData={subscriptionFormData}
        setSubscriptionFormData={setSubscriptionFormData}
        services={services}
        customerDetails={customerDetails}
        onCreateSubscription={handleCreateRecurringSubscription}
        onUpdateSubscription={handleUpdateRecurringSubscription}
        onCancelSubscription={handleCancelRecurringSubscription}
        onRefreshSubscriptions={fetchRecurringSubscriptions}
        onRefreshCustomerDetails={viewCustomerDetails}
        selectedCustomer={selectedCustomer}
      />

      {/* Add Child Dialog - Extracted Component */}
      <AddChildDialog
        open={addChildDialogOpen}
        onOpenChange={setAddChildDialogOpen}
        childFormData={childFormData}
        setChildFormData={setChildFormData}
        onAddChild={handleAddChild}
      />
      
      {/* Subscription Transaction History Modal - Extracted Component */}
      <SubscriptionHistoryDialog
        open={showSubscriptionHistoryModal}
        onOpenChange={setShowSubscriptionHistoryModal}
        loadingHistory={loadingHistory}
        subscriptionHistory={subscriptionHistory}
      />

      {/* Edit Services Dialog - Extracted Component */}
      <EditServicesDialog
        open={editServicesDialogOpen}
        onOpenChange={setEditServicesDialogOpen}
        editingPackage={editingPackage}
        services={services}
        selectedServiceIds={selectedServiceIds}
        setSelectedServiceIds={setSelectedServiceIds}
        customerDetailsId={customerDetails?.id}
        onSuccess={() => viewCustomerDetails(selectedCustomer)}
      />

      {/* Customer Merge Dialog */}
      <CardCampaignDialog open={cardCampaignOpen} onOpenChange={setCardCampaignOpen} />
      <CustomerMergeDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        customers={customers}
        onMergeComplete={fetchCustomers}
      />
      <DuplicatesDialog
        open={showDuplicates}
        onOpenChange={setShowDuplicates}
        onChanged={() => { fetchCustomers(); fetchDuplicateCount(); }}
      />

      {lightboxPhoto && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxPhoto(null)}
          data-testid="customer-photo-lightbox"
        >
          <img src={lightboxPhoto} alt="Foto gross" className="max-w-full max-h-full rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            data-testid="customer-photo-lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomersPage;
